import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { Table, TableStatus } from '../../types/tables';
import {
  abbreviateTableName,
  formatElapsedTime,
  formatMoneyCompact,
  isStaleTable,
  statusClassMap,
  statusLabelMap,
} from './tableHelpers';

interface TableFloorViewProps {
  tables: Table[];
  selectedTableId: string | null;
  /** "Todas": one block per area with its own header. */
  groupByArea: boolean;
  onSelect: (table: Table) => void;
  onOpen?: (table: Table) => void;
  /** Same actions as the list's "…" menu, reached with right click / long press. */
  getMenuItems: (table: Table) => MenuProps['items'];
}

const LEGEND: TableStatus[] = ['Available', 'Occupied', 'PreCheck', 'Reserved', 'Dirty'];

// Mantener presionado abre el menú de acciones. Se mide a mano porque Safari en iPhone/iPad
// no dispara `contextmenu` con el toque largo, y ahí el menú quedaría inalcanzable.
const LONG_PRESS_MS = 500;
const LONG_PRESS_SLOP_PX = 10;

interface AreaGroup {
  areaId: string;
  areaName: string;
  tables: Table[];
}

const groupTablesByArea = (tables: Table[]): AreaGroup[] => {
  const groups = new Map<string, AreaGroup>();
  for (const table of tables) {
    const group = groups.get(table.areaId)
      ?? { areaId: table.areaId, areaName: table.areaName, tables: [] };
    group.tables.push(table);
    groups.set(table.areaId, group);
  }
  return Array.from(groups.values());
};

const hasOpenOrder = (table: Table) => table.status === 'Occupied' || table.status === 'PreCheck';

/**
 * Vista compacta de /tables: un círculo por mesa, todos del mismo tamaño. El color es el
 * estado; adentro van el nombre (abreviado si no cabe) y las personas; debajo, el nombre
 * completo cuando se abrevió y el tiempo o el total.
 */
const TableFloorView: React.FC<TableFloorViewProps> = ({
  tables,
  selectedTableId,
  groupByArea,
  onSelect,
  onOpen,
  getMenuItems,
}) => {
  // Los tiempos de las mesas ocupadas avanzan solos, como en las tarjetas.
  const anyOccupied = tables.some(t => t.status === 'Occupied' && t.lastActivity);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!anyOccupied) return;
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, [anyOccupied]);

  const [menuTableId, setMenuTableId] = useState<string | null>(null);
  const press = useRef<{ timer: number | null; x: number; y: number; fired: boolean }>({
    timer: null, x: 0, y: 0, fired: false,
  });

  const cancelPress = () => {
    if (press.current.timer !== null) window.clearTimeout(press.current.timer);
    press.current.timer = null;
  };

  useEffect(() => cancelPress, []);

  const startPress = (tableId: string, e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return; // con mouse está el clic derecho
    cancelPress();
    press.current = {
      x: e.clientX,
      y: e.clientY,
      fired: false,
      timer: window.setTimeout(() => {
        press.current.fired = true;
        press.current.timer = null;
        setMenuTableId(tableId);
      }, LONG_PRESS_MS),
    };
  };

  const movePress = (e: React.PointerEvent) => {
    if (press.current.timer === null) return;
    // Si el dedo se desliza es scroll, no un toque largo.
    if (Math.hypot(e.clientX - press.current.x, e.clientY - press.current.y) > LONG_PRESS_SLOP_PX) cancelPress();
  };

  const groups = useMemo<AreaGroup[]>(
    () => (groupByArea ? groupTablesByArea(tables) : [{ areaId: '', areaName: '', tables }]),
    [groupByArea, tables]
  );

  // Mismo ruteo que TableCard: libre o reservada abre la mesa, lo demás la selecciona
  // (una secundaria unida la resuelve el padre hacia su principal).
  const handleClick = (table: Table) => {
    // El clic que sigue a un toque largo ya abrió el menú; no además abrir la mesa.
    if (press.current.fired) {
      press.current.fired = false;
      return;
    }
    if (table.mergedIntoTableId) {
      onSelect(table);
      return;
    }
    if (table.status === 'Available' || table.status === 'Reserved') {
      onOpen?.(table);
    } else {
      onSelect(table);
    }
  };

  const renderTable = (table: Table) => {
    const isMergedSecondary = !!table.mergedIntoTableId;
    const isMergedPrimary = (table.mergedTables?.length ?? 0) > 0;
    const stale = isStaleTable(table);
    const shortName = abbreviateTableName(table.number);
    const abbreviated = shortName !== table.number.trim();

    const totalCapacity = isMergedPrimary
      ? table.capacity + (table.mergedTables?.reduce((sum, t) => sum + t.capacity, 0) ?? 0)
      : table.capacity;
    const guests = hasOpenOrder(table) && table.guestCount
      ? `${table.guestCount}/${totalCapacity}`
      : `${totalCapacity} p.`;

    let detail: React.ReactNode = null;
    if (isMergedSecondary) {
      detail = <span className="tfloor-sub">Con Mesa {table.mergedIntoTableNumber}</span>;
    } else if (stale) {
      detail = <span className="tfloor-sub tfloor-sub--alert">¿Olvidada?</span>;
    } else if (table.status === 'Occupied') {
      detail = <span className="tfloor-sub">{formatElapsedTime(table.lastActivity)}</span>;
    } else if (table.status === 'PreCheck' && table.currentOrderTotal != null) {
      detail = <span className="tfloor-sub">{formatMoneyCompact(table.currentOrderTotal)}</span>;
    }

    const label = isMergedSecondary
      ? `Mesa ${table.number}, unida a la Mesa ${table.mergedIntoTableNumber}`
      : `Mesa ${table.number}, ${statusLabelMap[table.status]}`;

    const className = [
      'tfloor-table',
      `tfloor-${statusClassMap[table.status] || 'available'}`,
      stale ? 'tfloor-stale' : '',
      isMergedSecondary ? 'tfloor-merged-secondary' : '',
      selectedTableId === table.id ? 'selected' : '',
    ].filter(Boolean).join(' ');

    // Una mesa sucia no tiene orden que abrir: tocarla ofrece "Liberar Mesa" directo.
    const tapOpensMenu = table.status === 'Dirty' && !isMergedSecondary;

    return (
      <Dropdown
        key={table.id}
        menu={{ items: getMenuItems(table) }}
        trigger={tapOpensMenu ? ['click', 'contextMenu'] : ['contextMenu']}
        open={menuTableId === table.id}
        onOpenChange={(open) => setMenuTableId(open ? table.id : null)}
      >
        <button
          type="button"
          className={className}
          title={table.number}
          aria-label={label}
          onClick={tapOpensMenu ? undefined : () => handleClick(table)}
          onPointerDown={(e) => startPress(table.id, e)}
          onPointerMove={movePress}
          onPointerUp={cancelPress}
          onPointerLeave={cancelPress}
          onPointerCancel={cancelPress}
        >
          <span className="tfloor-circle">
            {table.status === 'PreCheck' && <span className="tfloor-ring" aria-hidden="true" />}
            {(isMergedPrimary || isMergedSecondary) && (
              <span className="tfloor-link" aria-hidden="true"><LinkOutlined /></span>
            )}
            <span className={`tfloor-name${shortName.length > 3 ? ' tfloor-name--long' : ''}`}>
              {shortName}
            </span>
            <span className="tfloor-guests">{guests}</span>
          </span>
          {abbreviated && <span className="tfloor-full">{table.number}</span>}
          {detail}
        </button>
      </Dropdown>
    );
  };

  return (
    <div className="tfloor">
      <div className="tfloor-plan">
        {groups.map(group => {
          const occupied = group.tables.filter(hasOpenOrder).length;
          return (
            <section key={group.areaId || 'all'} className="tfloor-area">
              {groupByArea && (
                <h4 className="tfloor-area-head">
                  {group.areaName}
                  <span>{occupied}/{group.tables.length} ocupadas</span>
                </h4>
              )}
              <div className="tfloor-grid">
                {group.tables.map(renderTable)}
              </div>
            </section>
          );
        })}
      </div>

      <div className="tfloor-legend" aria-hidden="true">
        {LEGEND.map(status => (
          <span key={status}>
            <i className={`tfloor-swatch tfloor-${statusClassMap[status]}`} />
            {statusLabelMap[status]}
          </span>
        ))}
        <span className="tfloor-legend-hint">Clic derecho o mantener presionado: más acciones</span>
      </div>
    </div>
  );
};

export default TableFloorView;
